import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Wrench, Users, Building2, FileSignature, LogOut, Sparkles, Wand2, Inbox } from "lucide-react";
import AICopilot from "@/components/AICopilot";

const NAV = [
  { to: "/app/dashboard", icon: LayoutGrid, label: "Dashboard", section: "dashboard" },
  { to: "/app/estimates", icon: Inbox, label: "Estimates", section: "estimates" },
  { to: "/app/services", icon: Wrench, label: "Services", section: "services" },
  { to: "/app/employees", icon: Users, label: "Employees", section: "employees" },
  { to: "/app/dealerships", icon: Building2, label: "Dealerships", section: "dealerships" },
  { to: "/app/signatures", icon: FileSignature, label: "Signatures", section: "signatures" },
];

const ADMIN_NAV = [
  { to: "/app/customize", icon: Wand2, label: "Customize", section: "customize" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Determine current section from path
  const path = window.location.pathname;
  const fullNav = user?.role === "admin" ? [...NAV, ...ADMIN_NAV] : NAV;
  const currentNav = fullNav.find((n) => path.startsWith(n.to)) || NAV[0];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-60 bg-black text-white flex-col fixed inset-y-0 z-30">
        <div className="p-6 border-b border-zinc-800">
          <div className="font-display text-xl tracking-tighter leading-tight">EAG Solutions <span className="text-[#FF2B00]">●</span></div>
          <div className="label-caps text-zinc-500 mt-1">{user?.role}</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 label-caps text-xs ${
                  isActive ? "bg-[#FF2B00] text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <Icon size={16} /> {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="label-caps text-zinc-600 px-3 pt-5 pb-2 text-[10px]">Admin</div>
              {ADMIN_NAV.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 label-caps text-xs ${
                      isActive ? "bg-[#FF2B00] text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                    }`
                  }
                >
                  <Icon size={16} /> {label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="nav-logout"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-3 text-zinc-400 hover:text-white hover:bg-zinc-900 label-caps text-xs"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-black text-white p-4 flex items-center justify-between">
        <div className="font-display text-xl">EAG <span className="text-[#FF2B00]">●</span></div>
        <button onClick={handleLogout} data-testid="mobile-logout"><LogOut size={18} /></button>
      </header>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-black text-white flex border-t border-zinc-900">
        {NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-2 ${isActive ? "text-[#FF2B00]" : "text-zinc-400"}`
            }
          >
            <Icon size={16} />
            <span className="text-[10px] uppercase">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Main content */}
      <main className="flex-1 lg:ml-60 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <Outlet />
        <AICopilot section={currentNav.section} />
      </main>
    </div>
  );
}
